import { type JSONDocNode, JSONTransformer } from '@atlaskit/editor-json-transformer';
import { vec2 } from 'gl-matrix';
import { DOMParser } from 'prosemirror-model';

import { createEnhancedSchema } from '@canvas/adf/create-enhanced-schema';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { backgroundInput, textDefaultColor } from '@canvas/constants/colors/base';
import {
  DEFAULT_TABLE_CELL_WIDTH,
  DEFAULT_TABLE_ROW_HEIGHT,
  MIN_TABLE_ROW_HEIGHT,
} from '@canvas/constants/elements/table';
import { defaultTextAlignment } from '@canvas/constants/elements/text';
import { getDefaultLocale } from '@canvas/constants/localization/localization-types';
import { UNSUPPORTED_BLOCK_TAGS } from '@canvas/constants/text';
import { type TextElementData, textElementType } from '@canvas/element-text';
import { boxCenter } from '@canvas/math/box';
import { type Board } from '@canvas/model/board';
import { elementType, type ElementData } from '@canvas/model/element';
import { type ConnectorElementData } from '@canvas/model/elements/connector';
import { connectorElementType } from '@canvas/model/elements/connector';
import { type PathElementData } from '@canvas/model/elements/path';
import { pathElementType } from '@canvas/model/elements/path';
import { pathLabelElementType } from '@canvas/model/elements/path-label';
import { smartConnectorElementType } from '@canvas/model/elements/smart-connector';
import { getPathSegmentsFromPoints } from '@canvas/path';
import { getBoundingBoxForPath } from '@canvas/path-utils/get-bounding-box-for-path';
import { Vector2 } from '@canvas/vector';

import { toConnectorStartAndEnd } from './connector-from-clipboard';

type SelectedElement = Omit<ElementData, 'id'>;

const isElementNegligibleForPositionCalculation = (element: SelectedElement) => {
  // Path label elements do not and should not impact the positions of paths.
  // This is because they are derived based on the endpoints/position of the
  // original path itself.
  return element.type !== pathLabelElementType;
};

export const getSelectionCenter = (
  selectedElements: SelectedElement[],
  elements: Board['elements'] = {}
): [number, number] => {
  const hasElements = Object.keys(elements).length > 0;

  const positions = selectedElements
    .filter(isElementNegligibleForPositionCalculation)
    .map((selectedElement) => {
      if (selectedElement.type === pathElementType) {
        const path = selectedElement as PathElementData;
        const pathBox = getBoundingBoxForPath(
          path.start,
          path.segments,
          path.stroke,
          path.startCap,
          path.endCap
        );
        const centre = boxCenter(pathBox);
        return Vector2.fromVec2(centre);
      }

      if (
        hasElements &&
        (selectedElement.type === connectorElementType ||
          selectedElement.type === smartConnectorElementType)
      ) {
        const connector = selectedElement as ConnectorElementData;

        const { start, end } = toConnectorStartAndEnd(connector, elements);
        const segments = getPathSegmentsFromPoints(start, end);

        const connectorBox = getBoundingBoxForPath(
          start,
          segments,
          connector.stroke,
          connector.startCap,
          connector.endCap
        );
        const centre = boxCenter(connectorBox);
        return Vector2.fromVec2(centre);
      }

      return selectedElement.position;
    });

  const allX = positions.map((p) => p.x);
  const allY = positions.map((p) => p.y);
  let maxX = allX[0];
  let maxY = allY[0];
  let minX = allX[0];
  let minY = allY[0];

  for (let i = 1; i < positions.length; i++) {
    maxX = Math.max(maxX, allX[i]);
    maxY = Math.max(maxY, allY[i]);
    minX = Math.min(minX, allX[i]);
    minY = Math.min(minY, allY[i]);
  }

  const centerX = (maxX - minX) / 2 + minX;
  const centerY = (maxY - minY) / 2 + minY;

  return [centerX, centerY];
};

export const getRelativeCoordinate = (origin: vec2, coord: vec2): vec2 => {
  return vec2.sub([0, 0], coord, origin);
};

const textElementDataFromAdfDoc = (adfDoc: JSONDocNode): Omit<TextElementData, 'id'> => ({
  type: textElementType,
  size: new Vector2(1, 1),
  text: JSON.stringify(adfDoc),
  position: new Vector2(0, 0),
  allowFlexibleWidth: true,
  color: textDefaultColor,
  basisSize: new Vector2(1, 1),
  basisPosition: new Vector2(0, 0),
  alignment: defaultTextAlignment,
});

const EMPTY_ADF_DOC = JSON.stringify({
  version: 1,
  type: 'doc',
  content: [],
});

const TABLE_CELL_HORIZONTAL_PADDING = 20;
const TABLE_CELL_VERTICAL_PADDING = 16;
const clipboardTableElementType = elementType('table');

type ClipboardTableRowDefinition = {
  id: string;
  height?: number;
};

type ClipboardTableColumnDefinition = {
  id: string;
  width?: number;
};

type ClipboardTableCellData = {
  rowId: string;
  colId: string;
  row: number;
  col: number;
  text: string;
};

export type ClipboardTableCellMeasurementInput = {
  row: number;
  col: number;
  adfContent: string;
  contentBoxWidth: number;
};

export type ClipboardTableCellMeasurementFn = (
  input: ClipboardTableCellMeasurementInput
) => Promise<number | undefined>;

export type HandleFormattedTableOptions = {
  measureCellContentHeight?: ClipboardTableCellMeasurementFn;
};

type ClipboardTableElementData = ElementData & {
  color: typeof backgroundInput;
  rows: number;
  cols: number;
  hasHeaderRow: boolean;
  hasHeaderColumn: boolean;
  rowOrder: string[];
  colOrder: string[];
  rowDefs?: Record<string, ClipboardTableRowDefinition>;
  colDefs?: Record<string, ClipboardTableColumnDefinition>;
  cellData: Record<string, ClipboardTableCellData>;
  basisPosition: Vector2;
  basisSize: Vector2;
};

// Placed outside to avoid re-creating the transformer and schema on every call
const transformer = new JSONTransformer();
// Create a custom schema with enhanced marks
const customSchema = createEnhancedSchema();
// Create a DOM serializer from our enhanced schema (DOM -> ProseMirror)
const domParser = DOMParser.fromSchema(customSchema);

const sanitizeHtmlBody = (htmlDoc: Document, bodyElement: HTMLElement) => {
  // Convert inline images to links so pasted content stays representable.
  const imgTags = Array.from(bodyElement.querySelectorAll('img'));
  imgTags.forEach((img) => {
    const altText = img.getAttribute('alt');
    const src = img.getAttribute('src');
    if (altText && src) {
      const linkElement = htmlDoc.createElement('a');
      linkElement.setAttribute('href', src);
      linkElement.textContent = altText;
      img.replaceWith(linkElement);
    } else if (altText) {
      const textNode = htmlDoc.createTextNode(altText);
      img.replaceWith(textNode);
    } else {
      img.remove();
    }
  });

  if (isFeatureFlagEnabled('fixPasteContentTruncation')) {
    // Convert date nodes to plain text since whiteboards don't support interactive dates
    const dateNodes = Array.from(
      bodyElement.querySelectorAll('span[data-node-type="date"]')
    );
    dateNodes.forEach((dateNode) => {
      const timestamp = dateNode.getAttribute('data-timestamp');
      if (!timestamp) {
        dateNode.remove();
        return;
      }

      const timestampNum = Number(timestamp);
      const date = new Date(timestampNum);

      // Explicitly check if the date is valid
      if (Number.isNaN(timestampNum) || Number.isNaN(date.getTime())) {
        dateNode.remove();
        return;
      }

      const formatter = new Intl.DateTimeFormat(getDefaultLocale(), {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
      const formattedDate = formatter.format(date);
      const textNode = htmlDoc.createTextNode(formattedDate);
      dateNode.replaceWith(textNode);
    });

    // Strip all style attributes from elements to prevent unsupported marks in ADF
    // This removes problematic inline styles from published pages (textColor, backgroundColor, etc.)
    const styledElements = Array.from(bodyElement.querySelectorAll('[style]'));
    styledElements.forEach((element) => {
      element.removeAttribute('style');
    });
  }
  const unsupportedBlockTags = Array.from(
    bodyElement.querySelectorAll(UNSUPPORTED_BLOCK_TAGS.join(', '))
  );
  unsupportedBlockTags.forEach((element) => {
    const parent = element.parentNode;
    if (parent) {
      while (element.firstChild) {
        parent.insertBefore(element.firstChild, element);
      }
      parent.removeChild(element);
    }
  });
};

const parseHtmlFragmentToAdfDoc = (fragmentHtml: string): JSONDocNode => {
  const htmlDoc = new globalThis.DOMParser().parseFromString(fragmentHtml, 'text/html');
  const bodyElement = htmlDoc.body;

  sanitizeHtmlBody(htmlDoc, bodyElement);

  const pmNode = domParser.parse(bodyElement);
  return transformer.encode(pmNode);
};

type ParsedHtmlTableCell = {
  isHeaderCell: boolean;
  text: string;
};

const parsePositiveNumber = (
  value: number | string | null | undefined
): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const parsed = Number.parseFloat(value.trim());
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const parsePixelWidth = (value: string | null): number | undefined => {
  if (!value) {
    return undefined;
  }

  const widthValue =
    value.match(/(?:^|;)\s*width\s*:\s*([^;]+)/i)?.[1] ??
    value.match(/(-?\d+(?:\.\d+)?)px/i)?.[0] ??
    value;

  const pixelMatch = widthValue.match(/(-?\d+(?:\.\d+)?)px/i);
  if (pixelMatch) {
    return parsePositiveNumber(pixelMatch[1]);
  }

  return parsePositiveNumber(widthValue);
};

const getStandaloneTableElement = (
  bodyElement: HTMLElement
): HTMLTableElement | undefined => {
  const tables = Array.from(bodyElement.querySelectorAll('table'));
  if (tables.length !== 1) {
    return undefined;
  }

  const clone = bodyElement.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('table').forEach((table) => table.remove());
  if ((clone.textContent ?? '').replace(/\s+/g, '').length > 0) {
    return undefined;
  }

  const [table] = tables;
  return table as HTMLTableElement;
};

const toPositiveSpan = (value: string | null): number => {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const parseTableCell = (cell: Element): ParsedHtmlTableCell => ({
  isHeaderCell: cell.tagName.toLowerCase() === 'th',
  text: JSON.stringify(parseHtmlFragmentToAdfDoc((cell as HTMLElement).innerHTML)),
});

const getTableCellKey = (rowId: string, colId: string) => `${rowId}:${colId}`;

const parseColWidthHints = (value: string | null): number[] => {
  if (!value) {
    return [];
  }

  return value
    .split(',')
    .map((part) => parsePositiveNumber(part))
    .filter((width): width is number => width !== undefined);
};

const setColumnWidthHint = (
  target: Array<number | undefined>,
  column: number,
  width: number | undefined
) => {
  if (width === undefined || column < 0) {
    return;
  }

  target[column] = width;
};

const getTableWidthHint = (tableElement: HTMLTableElement): number | undefined =>
  parsePositiveNumber(tableElement.getAttribute('data-table-width')) ??
  parsePositiveNumber(tableElement.getAttribute('width')) ??
  parsePixelWidth(tableElement.getAttribute('style'));

const getColgroupWidthHints = (
  tableElement: HTMLTableElement,
  columnCount: number
): Array<number | undefined> => {
  const hints = new Array<number | undefined>(columnCount).fill(undefined);
  const colElements = Array.from(tableElement.querySelectorAll('colgroup col')).slice(
    0,
    columnCount
  );

  colElements.forEach((colElement, index) => {
    setColumnWidthHint(
      hints,
      index,
      parsePixelWidth((colElement as HTMLElement).getAttribute('style')) ??
        parsePositiveNumber(colElement.getAttribute('width'))
    );
  });

  return hints;
};

const resolveColumnWidths = (
  maxColumnCount: number,
  tableWidthHint: number | undefined,
  cellWidthHints: Array<number | undefined>,
  colgroupWidthHints: Array<number | undefined>
): number[] => {
  const mergedHints = Array.from({ length: maxColumnCount }, (_, index) => {
    return cellWidthHints[index] ?? colgroupWidthHints[index];
  });
  const knownWidths = mergedHints.filter((width): width is number => width !== undefined);
  const knownTotal = knownWidths.reduce((sum, width) => sum + width, 0);
  const unknownCount = mergedHints.length - knownWidths.length;

  if (knownWidths.length === 0) {
    const fallbackWidth =
      tableWidthHint !== undefined
        ? tableWidthHint / maxColumnCount
        : DEFAULT_TABLE_CELL_WIDTH;
    return new Array(maxColumnCount).fill(fallbackWidth);
  }

  const averageKnownWidth = knownTotal / knownWidths.length;
  const remainingWidth =
    tableWidthHint !== undefined ? Math.max(tableWidthHint - knownTotal, 0) : undefined;
  const fallbackWidth =
    unknownCount > 0
      ? remainingWidth !== undefined && remainingWidth > 0
        ? remainingWidth / unknownCount
        : averageKnownWidth
      : DEFAULT_TABLE_CELL_WIDTH;

  const resolvedWidths = mergedHints.map((width) => width ?? fallbackWidth);
  if (tableWidthHint === undefined) {
    return resolvedWidths;
  }

  const resolvedTotal = resolvedWidths.reduce((sum, width) => sum + width, 0);
  if (resolvedTotal <= 0) {
    return resolvedWidths;
  }

  const scale = tableWidthHint / resolvedTotal;
  return resolvedWidths.map((width) => width * scale);
};

const buildColumnDefinitions = (
  colOrder: string[],
  colWidths: number[]
): Record<string, ClipboardTableColumnDefinition> =>
  Object.fromEntries(
    colOrder.map((colId, index) => [colId, { id: colId, width: colWidths[index] }])
  );

const buildRowDefinitions = (
  rowOrder: string[],
  rowHeights: number[]
): Record<string, ClipboardTableRowDefinition> =>
  Object.fromEntries(
    rowOrder.map((rowId, index) => [rowId, { id: rowId, height: rowHeights[index] }])
  );

export const handleFormattedTable = (
  content: string,
  options: HandleFormattedTableOptions = {}
): Promise<Omit<ClipboardTableElementData, 'id'>[] | undefined> => {
  try {
    const htmlDoc = new globalThis.DOMParser().parseFromString(content, 'text/html');
    const bodyElement = htmlDoc.body;
    if (!bodyElement) {
      return Promise.resolve(undefined);
    }

    sanitizeHtmlBody(htmlDoc, bodyElement);

    const tableElement = getStandaloneTableElement(bodyElement);
    if (!tableElement) {
      return Promise.resolve(undefined);
    }

    const rows = Array.from(tableElement.querySelectorAll('tr'));
    if (rows.length === 0) {
      return Promise.resolve(undefined);
    }

    const parsedRows: Array<Array<ParsedHtmlTableCell | null | undefined>> = [];
    const cellWidthHints: Array<number | undefined> = [];
    let maxColumnCount = 0;

    rows.forEach((row, rowIndex) => {
      parsedRows[rowIndex] ??= [];

      let columnIndex = 0;
      const cells = Array.from(row.children).filter((child) => {
        const tagName = child.tagName.toLowerCase();
        return tagName === 'td' || tagName === 'th';
      });

      cells.forEach((cell) => {
        while (parsedRows[rowIndex][columnIndex] !== undefined) {
          columnIndex += 1;
        }

        parsedRows[rowIndex][columnIndex] = parseTableCell(cell);

        const colSpan = toPositiveSpan(cell.getAttribute('colspan'));
        const rowSpan = toPositiveSpan(cell.getAttribute('rowspan'));
        const dataColWidths = parseColWidthHints(cell.getAttribute('data-colwidth'));
        if (dataColWidths.length === colSpan) {
          dataColWidths.forEach((width, widthIndex) => {
            setColumnWidthHint(cellWidthHints, columnIndex + widthIndex, width);
          });
        } else if (dataColWidths.length === 1) {
          const width = dataColWidths[0] / colSpan;
          for (let widthIndex = 0; widthIndex < colSpan; widthIndex++) {
            setColumnWidthHint(cellWidthHints, columnIndex + widthIndex, width);
          }
        } else if (colSpan === 1) {
          setColumnWidthHint(
            cellWidthHints,
            columnIndex,
            parsePixelWidth((cell as HTMLElement).getAttribute('style')) ??
              parsePositiveNumber((cell as HTMLElement).getAttribute('width'))
          );
        }

        for (let spanRow = rowIndex; spanRow < rowIndex + rowSpan; spanRow++) {
          parsedRows[spanRow] ??= [];

          for (
            let spanColumn = columnIndex;
            spanColumn < columnIndex + colSpan;
            spanColumn++
          ) {
            if (spanRow === rowIndex && spanColumn === columnIndex) {
              continue;
            }

            parsedRows[spanRow][spanColumn] = null;
          }
        }

        columnIndex += colSpan;
      });

      maxColumnCount = Math.max(maxColumnCount, parsedRows[rowIndex].length);
    });

    if (maxColumnCount === 0) {
      return Promise.resolve(undefined);
    }

    const tableWidthHint = getTableWidthHint(tableElement);
    const colgroupWidthHints = getColgroupWidthHints(tableElement, maxColumnCount);
    const colWidths = resolveColumnWidths(
      maxColumnCount,
      tableWidthHint,
      cellWidthHints,
      colgroupWidthHints
    );
    const rowOrder = Array.from(
      { length: parsedRows.length },
      (_, index) => `row-${index}`
    );
    const colOrder = Array.from({ length: maxColumnCount }, (_, index) => `col-${index}`);

    const hasHeaderRow = parsedRows[0]
      .slice(0, maxColumnCount)
      .every((cell) => cell === null || cell?.isHeaderCell === true);

    const hasHeaderColumn =
      parsedRows.length > (hasHeaderRow ? 1 : 0) &&
      parsedRows
        .slice(hasHeaderRow ? 1 : 0)
        .every((row) => row[0] === null || row[0]?.isHeaderCell === true);

    const cellData = rowOrder.reduce<Record<string, ClipboardTableCellData>>(
      (result, rowId, row) => {
        const rowCells = parsedRows[row] ?? [];
        colOrder.forEach((colId, col) => {
          const cell = rowCells[col];
          result[getTableCellKey(rowId, colId)] = {
            rowId,
            colId,
            row,
            col,
            text: cell?.text ?? EMPTY_ADF_DOC,
          };
        });

        return result;
      },
      {}
    );
    const colDefs = buildColumnDefinitions(colOrder, colWidths);

    return Promise.all(
      rowOrder.map(async (rowId, row) => {
        const { measureCellContentHeight } = options;
        if (!measureCellContentHeight) {
          return {
            rowId,
            height: DEFAULT_TABLE_ROW_HEIGHT,
          };
        }

        let rowHeight = MIN_TABLE_ROW_HEIGHT;
        await Promise.all(
          colOrder.map(async (colId, col) => {
            const cell = cellData[getTableCellKey(rowId, colId)];
            if (!cell || cell.text === EMPTY_ADF_DOC) {
              return;
            }

            const contentBoxWidth = Math.max(
              colWidths[col] - TABLE_CELL_HORIZONTAL_PADDING,
              1
            );
            const measuredHeight = await measureCellContentHeight({
              row,
              col,
              adfContent: cell.text,
              contentBoxWidth,
            }).then(
              (height) => height,
              () => undefined
            );

            if (measuredHeight && measuredHeight > 0) {
              rowHeight = Math.max(
                rowHeight,
                measuredHeight + TABLE_CELL_VERTICAL_PADDING
              );
            }
          })
        );

        return { rowId, height: rowHeight };
      })
    ).then((measuredRows) => {
      const rowHeights = measuredRows.map(({ height }) => height);
      const rowDefs =
        options.measureCellContentHeight !== undefined
          ? buildRowDefinitions(rowOrder, rowHeights)
          : undefined;
      const tableWidth = colWidths.reduce((sum, width) => sum + width, 0);
      const tableHeight =
        rowDefs !== undefined
          ? rowHeights.reduce((sum, height) => sum + height, 0)
          : DEFAULT_TABLE_ROW_HEIGHT * parsedRows.length;

      return [
        {
          type: clipboardTableElementType,
          position: new Vector2(0, 0),
          size: new Vector2(tableWidth, tableHeight),
          basisPosition: new Vector2(0, 0),
          basisSize: new Vector2(tableWidth, tableHeight),
          color: backgroundInput,
          rows: parsedRows.length,
          cols: maxColumnCount,
          hasHeaderRow,
          hasHeaderColumn,
          rowOrder,
          colOrder,
          ...(rowDefs ? { rowDefs } : {}),
          colDefs,
          cellData,
        },
      ];
    });
  } catch (_error) {
    return Promise.resolve(undefined);
  }
};

export const handleFormattedText = (content: string) => {
  try {
    const adfDoc = parseHtmlFragmentToAdfDoc(content);
    const elementData = [textElementDataFromAdfDoc(adfDoc) as TextElementData];
    return elementData;
  } catch (_error) {
    // This is fine as it will eventually default to plain text if the ADF is invalid
    return undefined;
  }
};
